import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGuidaUIStates, DynamicGuide } from '@inter-ikea-guida/framework';
import { SkapaPill, SkapaText } from '@inter-ikea-guida/skapa';
import { useGuidaNavigation, useInsights } from '../../contexts';
import { useIsKiosk, useLocale, usePresetStoreId } from '../../contexts/environment';
import { useKompis } from '../../contexts/kompis';
import { useSelectOption } from '../../hooks';
import useIsRtl from '../../hooks/useIsRtl';
import { useAppDispatch } from '../../store';
import { selectRadio, setSorting, setStores } from '../../store/app';
import { getAppState, getCMSState, getFormState, getSorting } from '../../store/app/selectors';
import { getTranslation } from '../../store/i18n/selectors';
import { CORNER_WALL, DOOR_TYPE, SELECT, SINGLE_WALL, SIZE_DEPTH, SIZE_DEPTH_LEFT, SIZE_DEPTH_RIGHT, SIZE_HEIGHT, SIZE_WIDTH, WALL, getStores } from '../../utils';
import { getTranslationKeyFromSortingEnum, SortingEnum } from '../../utils/enums/SortingEnum';
import { sendInsightsEvent } from '../../utils/insights/sendInsights';
import { isEmpty } from '../../utils/isEmpty';
import { setWidthDataFromCmsData } from '../../views/viewUtils';
import { PaxLayout } from '../common/PaxLayout';
import Products from '../Products/Products';
import { FilterGuide } from './FilterGuide';
import { NeedsGuide } from './NeedsGuide';
import { StyledMain, StyledPills, StyledHeading, StyledCopy, StyledGuideContainer, StyledSubHeading } from './styles';
export const Results = () => {
    const isKiosk = useIsKiosk();
    const dispatch = useAppDispatch();
    const translate = useSelector(getTranslation);
    const insights = useInsights().moduleClient;
    const selectOption = useSelectOption();
    const locale = useLocale();
    const { dexfAppSettings } = useKompis();
    const presetStoreId = usePresetStoreId();
    const { isCompact, isSidebarMode } = useGuidaUIStates();
    const { storeId, stores } = useSelector(getAppState);
    const sorting = useSelector(getSorting);
    const formState = useSelector(getFormState);
    const cmsState = useSelector(getCMSState);
    const { pathname } = location;
    const isRtl = useIsRtl();
    const guidaNavigation = useGuidaNavigation();
    const [isSheetOpen, setIsSheetOpen] = useState(!isSidebarMode);
    const [scrollToNeeds, setScrollToNeeds] = useState(false);
    const [sheetHasAnimated, setSheetHasAnimated] = useState(false);
    const updateSorting = () => {
        if (sorting === SortingEnum.BestFit) {
            dispatch(setSorting(SortingEnum.PriceAsc));
            return;
        }
        if (sorting === SortingEnum.PriceAsc) {
            dispatch(setSorting(SortingEnum.PriceDesc));
            return;
        }
        dispatch(setSorting(SortingEnum.BestFit));
    };
    const openSection = (section) => {
        !isSidebarMode && setIsSheetOpen(true);
        sendInsightsEvent('whatIsMe', `open_${section}`);
        // Scroll to storage needs if that pill is selected.
        setScrollToNeeds(section === 'storage_needs');
    };
    useEffect(() => {
        if (!isSheetOpen) {
            setSheetHasAnimated(false);
        }
    }, [isSheetOpen]);
    useEffect(() => {
        insights?.sendUserFlowStep('user-guide-flow', 'results', 2);
    }, [insights]);
    useEffect(() => {
        if (isEmpty(stores)) {
            getStores(dexfAppSettings, locale).then(result => {
                dispatch(setStores(result));
            });
        }
    }, [dispatch, locale, dexfAppSettings, stores]);
    const selectInitialOption = useCallback((id, index) => {
        const formItem = cmsState.form.find(item => item.id === id);
        const initialOption = formItem?.options[Math.min(index, formItem?.options.length - 1)];
        const type = cmsState.form.find(item => item.id === id)?.type;
        if (initialOption && type) {
            const initialIndex = formItem.options.length > 1 || !formItem.id.includes('height') ? index : 0;
            selectOption(id, initialOption.value, type, initialIndex);
        }
    }, [cmsState.form, selectOption]);
    // Select single wall as initial value
    useEffect(() => {
        if (!formState.wall) {
            dispatch(selectRadio({
                id: WALL,
                value: SINGLE_WALL
            }));
        }
    }, [dispatch, formState.wall]);
    useEffect(() => {
        if (formState.wall === SINGLE_WALL) {
            const ids = [WALL, DOOR_TYPE, SIZE_DEPTH, SIZE_HEIGHT];
            const idsIndex = [0, 0, 1, 1];
            ids.forEach((id, index) => {
                if (!formState[id]) {
                    selectInitialOption(id, idsIndex[index]);
                }
            });
            const width = cmsState.form.find(item => item.id === SIZE_WIDTH)?.start.toString();
            if (!formState[SIZE_WIDTH] && width) {
                const index = cmsState.form
                    .find(item => item.id === SIZE_WIDTH)
                    ?.options.findIndex(option => option.value === width);
                selectOption(SIZE_WIDTH, width, SELECT, index);
            }
        }
        else if (formState.wall === CORNER_WALL) {
            const ids = [WALL, DOOR_TYPE, SIZE_DEPTH_LEFT, SIZE_DEPTH_RIGHT, SIZE_HEIGHT];
            const idsIndex = [1, 0, 1, 1, 1];
            ids.forEach((id, index) => {
                if (!formState[id]) {
                    selectInitialOption(id, idsIndex[index]);
                }
            });
            // Set form state data from cms if the data is not already set in the formState
            setWidthDataFromCmsData(formState, cmsState.form, selectOption);
        }
    }, [formState, cmsState.form, selectOption, selectInitialOption, pathname]);
    const handleBack = () => {
        sendInsightsEvent('whatIsMe', 'quit');
        guidaNavigation.goToLandingPage();
    };
    const backButtonText = translate('Common.Exit');
    return (_jsx(_Fragment, { children: _jsx(PaxLayout, { storeId: storeId, stores: presetStoreId ? undefined : stores, backButtonText: backButtonText.substring(0, 20), onBackButtonClick: handleBack, isRtl: isRtl, isKiosk: isKiosk, children: _jsx(DynamicGuide, { isSheetVisible: isSheetOpen, setIsSheetVisible: setIsSheetOpen, sheetHeading: translate('guid01FwGuidingStep1'), sheetButtonText: translate('guid01FwBtnDone'), onSheetOpened: () => {
                    setSheetHasAnimated(true);
                }, guide: _jsxs(StyledGuideContainer, { "$isSidebarMode": isSidebarMode, children: [_jsx("span", { role: 'note', "aria-label": 'Settings', tabIndex: 0, "data-autofocus": true }), _jsx(FilterGuide, {}), _jsx(NeedsGuide, { scrollToGuide: sheetHasAnimated && scrollToNeeds })] }), headerHeight: 96, children: _jsxs(StyledMain, { children: [_jsxs(StyledCopy, { "$isCompact": isCompact, children: [_jsx(StyledHeading, { children: _jsx(SkapaText, { textStyle: isCompact ? 'Heading.S' : 'Heading.L', tagName: 'h1', children: translate('guid01FwBodyHeadlineSameNeeds') }) }), _jsx(StyledSubHeading, { children: _jsx(SkapaText, { textStyle: isCompact ? 'Body.M' : 'Body.L', children: translate('guid01ReHeadline') }) })] }), _jsxs(StyledPills, { children: [_jsx(SkapaPill, { dataTestId: 'sorting-pill', label: `${translate('Sorting.Label')}: ${translate(getTranslationKeyFromSortingEnum(sorting))}`, onClick: updateSorting, selected: sorting !== SortingEnum.BestFit, size: 'small' }), !isSidebarMode && (_jsxs(_Fragment, { children: [_jsx(SkapaPill, { dataTestId: 'change-details', selected: isSheetOpen && !scrollToNeeds, label: translate('guid01FwGuidingStep1'), onClick: () => openSection('layout_and_size'), size: 'small' }), _jsx(SkapaPill, { dataTestId: 'change-needs', selected: isSheetOpen && scrollToNeeds, label: translate('guid01FwGuidingStep2BtnLabel'), onClick: () => openSection('storage_needs'), size: 'small' })] }))] }), _jsx(Products, { isMobile: false })] }) }) }) }));
};
//# sourceMappingURL=Results.js.map